import { useState } from 'react'
import { Link } from 'react-router-dom'
import {Mail, Send, MessageSquare, ShieldCheck, Clock, CheckCircle2} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

function XLogo({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}
import Layout from '@/components/Layout'

const C = {
  bgDeep:     '#000000',
  surface:    '#070a0c',
  bgCard:     '#0a0e12',
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.10)',
  turqBright: '#3ce8d6',
  borderTurq: 'rgba(26,214,196,0.28)',
  border:     'rgba(255,255,255,0.07)',
  borderMed:  'rgba(255,255,255,0.13)',
  textSecond: '#b4c0cc',
  textMuted:  '#6a7682',
  error:      '#f87171',
  errorDim:   'rgba(248,113,113,0.10)',
}

const inputBase: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${C.borderMed}`,
  color: '#ffffff',
  width: '100%',
  borderRadius: 10,
  padding: '12px 14px',
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 0.15s',
}

const TOPICS = [
  'General Inquiry',
  'Request an Audit',
  'Partnership / Collaboration',
  'Report an Issue',
  'Press / Media',
  'Other',
]

const CONTACT_ITEMS = [
  {
    icon: Mail,
    label: 'Email',
    value: 'MandoxAudits@gmail.com',
    href: 'mailto:MandoxAudits@gmail.com',
  },
  {
    icon: XLogo,
    label: 'Twitter / X',
    value: '@Mandox_Audits',
    href: 'https://x.com/Mandox_Audits',
  },
  {
    icon: Send,
    label: 'Telegram',
    value: 't.me/officialmandox',
    href: 'https://t.me/officialmandox',
  },
]

const RESPONSE_INFO = [
  { icon: Clock,        text: 'We typically respond within 24–48 business hours.' },
  { icon: ShieldCheck,  text: 'Audit inquiries are reviewed by our security team.' },
  { icon: MessageSquare,text: 'For urgent issues, reach us directly on Telegram.' },
]

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export default function Contact() {
  const { user, isAuthenticated } = useAuth()
  const [form, setForm] = useState({ name: '', email: '', topic: TOPICS[0], message: '' })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [emailed, setEmailed]   = useState(false)

  const set = (k: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm(p => ({ ...p, [k]: e.target.value }))

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all required fields.')
      return
    }
    setLoading(true)

    const safeName    = escapeHtml(form.name.trim())
    const safeEmail   = escapeHtml(form.email.trim())
    const safeTopic   = escapeHtml(form.topic)
    const safeMessage = escapeHtml(form.message.trim()).replace(/\n/g, '<br/>')

    const confirmationHtml = `
      <div style="font-family:Arial,Helvetica,sans-serif;background:#000;padding:32px;color:#ffffff;">
        <h2 style="color:#1ad6c4;margin:0 0 16px;">We received your message</h2>
        <p style="color:#b4c0cc;line-height:1.6;margin:0 0 20px;">
          Hi ${safeName}, thanks for reaching out to <strong style="color:#ffffff;">Crypto Audit Service</strong>.
          Our team will reply within 24–48 business hours. Here is a copy of what you sent:
        </p>
        <div style="border-left:3px solid #1ad6c4;padding:12px 16px;background:rgba(26,214,196,0.06);">
          <p style="margin:0 0 8px;color:#6a7682;font-size:13px;">Topic</p>
          <p style="margin:0 0 16px;color:#ffffff;">${safeTopic}</p>
          <p style="margin:0 0 8px;color:#6a7682;font-size:13px;">Message</p>
          <p style="margin:0;color:#ffffff;line-height:1.6;">${safeMessage}</p>
        </div>
        <p style="color:#6a7682;font-size:12px;margin:24px 0 0;">
          Crypto Audit Service · Smart contract audits for EVM tokens
        </p>
      </div>
    `

    try {
      // lumi.tools.email.send can only deliver to registered users.
      // When the visitor is signed in, send them a confirmation copy of their request.
      if (isAuthenticated && user?.email) {
        await lumi.tools.email.send({
          to: user.email,
          subject: `Crypto Audit Service — we received your message (${form.topic})`,
          fromName: 'Crypto Audit Service',
          html: confirmationHtml,
        })
        setEmailed(true)
      } else {
        setEmailed(false)
      }
      setSubmitted(true)
    } catch (err) {
      console.error('Contact email send failed:', err)
      // Still confirm receipt so the visitor experience is not blocked.
      setEmailed(false)
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      {/* Header */}
      <section style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-[900px] mx-auto px-5 py-16 text-center">
          <span
            className="text-xs font-semibold tracking-widest uppercase mb-3 inline-block"
            style={{ color: C.turq, letterSpacing: '0.12em' }}
          >
            Contact Us
          </span>
          <h1
            className="text-white font-light leading-tight mb-4"
            style={{ fontSize: 'clamp(28px, 5vw, 48px)', letterSpacing: '-1px' }}
          >
            Get in Touch
          </h1>
          <p className="text-sm max-w-md mx-auto leading-relaxed" style={{ color: C.textMuted }}>
            Have a question about an audit, a partnership opportunity, or just want to say hello?
            We'd love to hear from you.
          </p>
        </div>
      </section>

      <section style={{ background: C.bgDeep }} className="py-16">
        <div className="max-w-[900px] mx-auto px-5">
          <div className="grid lg:grid-cols-[1fr_300px] gap-14">

            {/* ── Left: form ── */}
            <div>
              {submitted ? (
                <div
                  className="rounded-2xl p-10 flex flex-col items-center text-center gap-5"
                  style={{ background: C.bgCard, border: `1px solid ${C.borderTurq}` }}
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                  >
                    <CheckCircle2 className="w-8 h-8" style={{ color: C.turq }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">Message Sent!</h2>
                    <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>
                      Thanks, <span className="text-white">{form.name}</span>! We've received your message
                      and will respond to <span style={{ color: C.turq }}>{form.email}</span> within 24–48 hours.
                    </p>
                    {emailed && (
                      <p className="text-xs leading-relaxed mt-2" style={{ color: C.turq }}>
                        A confirmation copy has been emailed to your account address.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => { setSubmitted(false); setEmailed(false); setForm({ name: '', email: '', topic: TOPICS[0], message: '' }) }}
                    className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110"
                    style={{ background: C.turq, color: '#000' }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  {error && (
                    <div
                      className="rounded-lg px-4 py-3 text-sm"
                      style={{ background: C.errorDim, border: `1px solid rgba(248,113,113,0.2)`, color: C.error }}
                    >
                      {error}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium" style={{ color: C.textSecond }}>
                        Full Name <span style={{ color: C.error }}>*</span>
                      </label>
                      <input
                        value={form.name}
                        onChange={set('name')}
                        placeholder="Your name"
                        required
                        style={inputBase}
                      />
                    </div>

                    {/* Email */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium" style={{ color: C.textSecond }}>
                        Email Address <span style={{ color: C.error }}>*</span>
                      </label>
                      <div className="relative">
                        <Mail
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                          style={{ color: C.turq }}
                        />
                        <input
                          type="email"
                          value={form.email}
                          onChange={set('email')}
                          placeholder="you@example.com"
                          required
                          style={{ ...inputBase, paddingLeft: 40 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Topic */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: C.textSecond }}>Topic</label>
                    <select value={form.topic} onChange={set('topic')} style={inputBase}>
                      {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: C.textSecond }}>
                      Message <span style={{ color: C.error }}>*</span>
                    </label>
                    <textarea
                      value={form.message}
                      onChange={set('message')}
                      rows={6}
                      placeholder="Tell us what you'd like to discuss…"
                      required
                      style={{ ...inputBase, resize: 'none' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed w-fit"
                    style={{ background: C.turq, color: '#000000' }}
                  >
                    <Send className="w-4 h-4" />
                    {loading ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* ── Right: contact info ── */}
            <div className="flex flex-col gap-8">
              {/* Direct contacts */}
              <div>
                <p
                  className="text-[11px] font-semibold uppercase tracking-widest mb-5"
                  style={{ color: C.turq, letterSpacing: '0.14em' }}
                >
                  Direct Contact
                </p>
                <div className="flex flex-col gap-4">
                  {CONTACT_ITEMS.map(({ icon: Icon, label, value, href }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 group transition-colors"
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: C.turq }} />
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: C.textMuted }}>{label}</p>
                        <p
                          className="text-sm font-medium transition-colors group-hover:text-white"
                          style={{ color: C.textSecond }}
                        >
                          {value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px" style={{ background: C.border }} />

              {/* Response info */}
              <div>
                <p
                  className="text-[11px] font-semibold uppercase tracking-widest mb-4"
                  style={{ color: C.turq, letterSpacing: '0.14em' }}
                >
                  What to Expect
                </p>
                <div className="flex flex-col gap-3">
                  {RESPONSE_INFO.map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-start gap-2.5">
                      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.turq }} />
                      <p className="text-xs leading-relaxed" style={{ color: C.textMuted }}>{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px" style={{ background: C.border }} />

              {/* Quick links */}
              <div>
                <p
                  className="text-[11px] font-semibold uppercase tracking-widest mb-4"
                  style={{ color: C.turq, letterSpacing: '0.14em' }}
                >
                  Quick Links
                </p>
                <div className="flex flex-col gap-2">
                  <Link
                    to="/pricing"
                    className="text-sm transition-colors hover:text-white"
                    style={{ color: C.textMuted }}
                  >
                    View Audit Packages →
                  </Link>
                  <Link
                    to="/projects"
                    className="text-sm transition-colors hover:text-white"
                    style={{ color: C.textMuted }}
                  >
                    Browse Audited Projects →
                  </Link>
                  <Link
                    to="/auth"
                    className="text-sm transition-colors hover:text-white"
                    style={{ color: C.textMuted }}
                  >
                    Create an Account →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}
